import React, { useMemo } from 'react'
import { enums } from '@adc/sdk'
import * as graph from '@adc/sdk'
import { CurrentTargetAudience, Listing } from '@adc/sdk/types'
import { Text, Money, utils } from '@adc/ui-core'
import styles from './styles.scss'
import classnames from 'classnames/bind'
import { formatOfferDate } from '../../../util/formatters'
import moment from 'moment'

const cx = classnames.bind(styles)

type AuctionDetailsProps = {
  listing: Listing
  viewingFrom?: CurrentTargetAudience
}
const AuctionDetails = ({
  listing,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
}: AuctionDetailsProps) => {
  const formattedStartDate = formatOfferDate(listing?.auction?.start_date)
  const formattedEndDate = formatOfferDate(listing?.auction?.end_date)
  const reviewDate = formatOfferDate(
    listing?.online_auction_segment?.configuration?.review_date
  )
  const secondsOnAuction = moment().diff(
    listing?.auction?.start_date,
    'seconds'
  )
  // Minimum count of days will always be '1 day'
  const formattedDaysOnAuction =
    secondsOnAuction < 86400 // less than one day
      ? '1 day'
      : utils.countdown.formatPeriodTime(
          secondsOnAuction,
          viewingFrom === graph.enums.CurrentTargetAudience.Seller
            ? 'days'
            : 'weeks',
          'days'
        )

  const isSealedBidAndBuyer =
    (listing?.online_auction_segment?.segment_type ===
      graph.enums.OnlineAuctionSegmentType.SealedBid ||
      (listing?.selling_method?.__typename === 'OnlineAuctionSegment' &&
        listing?.selling_method?.segment_type ===
          graph.enums.OnlineAuctionSegmentType.SealedBid)) &&
    viewingFrom === graph.enums.CurrentTargetAudience.Buyer

  const displayAsListPrice =
    listing?.listing_configuration?.product_type ===
      enums.ProductType.PrivateSeller || isSealedBidAndBuyer

  return useMemo(
    () => (
      <div
        className={cx(
          'details-container',
          isSealedBidAndBuyer ? 'sealed-bid' : ''
        )}
      >
        <div className={cx('detail-row')}>
          <div className={cx('detail')}>
            <Text size='tiny' color='grayMedium' className={cx('label-text')}>
              {`${displayAsListPrice ? 'List' : 'Your Listing'} Price${
                isSealedBidAndBuyer ? ':' : ''
              }`}
            </Text>
            <Money
              weight='heavy'
              size='tiny'
              color='gray'
              amount={listing?.valuation?.seller_current_value_amount ?? 0}
            />
          </div>
        </div>
        {!isSealedBidAndBuyer && (
          <>
            <div className={cx('detail-row')}>
              <div className={cx('detail')}>
                <Text
                  size='tiny'
                  color='grayMedium'
                  className={cx('label-text')}
                >
                  Listing Start Date
                </Text>
                <Text
                  weight='heavy'
                  size='tiny'
                  color='gray'
                  data-elm-id='listing-start_date'
                >
                  {formattedStartDate}
                </Text>
              </div>
              <div className={cx('detail')}>
                <Text
                  size='tiny'
                  color='grayMedium'
                  className={cx('label-text')}
                >
                  Listing End Date
                </Text>
                <Text
                  weight='heavy'
                  size='tiny'
                  color='gray'
                  data-elm-id='listing-end_date'
                >
                  {formattedEndDate}
                </Text>
              </div>
            </div>
            <div className={cx('detail-row')}>
              {listing?.online_auction_segment?.configuration?.review_date && (
                <div className={cx('detail')}>
                  <Text
                    size='tiny'
                    color='grayMedium'
                    className={cx('label-text')}
                  >
                    Reviewing Offer Date
                  </Text>
                  <Text
                    weight='heavy'
                    size='tiny'
                    color='gray'
                    data-elm-id='listing-start_date'
                  >
                    {reviewDate}
                  </Text>
                </div>
              )}
              <div className={cx('detail')}>
                <Text
                  size='tiny'
                  color='grayMedium'
                  className={cx('label-text')}
                >
                  Days on Market
                </Text>
                <Text
                  weight='heavy'
                  size='tiny'
                  color='gray'
                  data-elm-id='listing-days_on_auction'
                >
                  {formattedDaysOnAuction}
                </Text>
              </div>
            </div>
          </>
        )}
      </div>
    ),
    [listing, viewingFrom]
  )
}

export default AuctionDetails
